import { IconLoader2 } from "@tabler/icons-react";
import { PInfo } from "@/components/PInfo/PInfo";
import { useParentPosition } from "@/hooks/useParentPosition";
import { useState, useEffect, useId, useRef } from "react";

type SecondaryButtonProps = {
  text: string; // Button text
  onClick?: (e: React.MouseEvent) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
  onMouseEnter?: (e: React.MouseEvent) => void;
  icon?: React.ElementType; // Icon before text
  iconSize?: number; // Icon size
  iconPosition?: "left" | "right"; // Icon position relative to text
  size?: "big" | "medium" | "small" | "xsmall" | "xssmall" | "icon"; // Sizes
  color?: string; // Color
  borderColor?: string; // Border color
  textColor?: string; // Text color
  width?: string; // Width
  margin?: string; // Margin
  padding?: string; // Padding
  disabled?: boolean; // Disabled state
  loading?: boolean; // Loading state
  rounded?: string;
  fontType?: string; // Font type
  iconMargin?: string; // Icon margin
  className?: string; // Additional class names
  height?: string; // Height
  customPadding?: string;
  disabledMessage?: string; // Message to show when disabled
  testId?: string;
  /** Accessible name — required for icon-only buttons (text=""). Also set as
   *  the native title for a hover tooltip. */
  ariaLabel?: string;
  /** Id of the element explaining WHY this button is disabled. Mirrors
   *  PrimaryButton.ariaDescribedBy. This button uses `aria-disabled` rather
   *  than a native `disabled`, so it stays focusable and the description is
   *  actually reachable. */
  ariaDescribedBy?: string;
  /** `aria-busy` — work in flight behind this button (a retry while the reads run). Opt-in, like IconButton's. */
  ariaBusy?: boolean;
};

export const SecondaryButton: React.FC<SecondaryButtonProps> = ({
  text,
  onClick,
  onMouseEnter,
  onMouseLeave,
  icon: Icon,
  iconSize = 20,
  iconPosition = "left",
  size = "small",
  color = "",
  borderColor = "outline-secondary",
  textColor = "font-primary",
  width = "w-full",
  margin = "",
  padding = "",
  loading = false,
  disabled = false,
  rounded,
  fontType,
  iconMargin = "mr-1",
  className = "",
  height = "h-max",
  customPadding = "",
  disabledMessage = "",
  testId,
  ariaLabel,
  ariaDescribedBy,
  ariaBusy,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // The disabled reason must be reachable without the hover tooltip: the
  // button names it in aria-describedby — the tooltip while it is shown, a
  // hidden copy otherwise, so exactly one node carries it at a time (the
  // tooltip had no id, so nothing referenced it — Stripe go-live re-audit r3).
  const reasonId = useId();
  const announcesReason = disabled && !!disabledMessage;
  const describedBy =
    [ariaDescribedBy, announcesReason ? reasonId : undefined].filter(Boolean).join(" ") || undefined;
  const { dropdownPosition, setDropdownPosition } = useParentPosition();

  const sizeStyles = {
    big: `${customPadding ? customPadding : "px-6 py-2.75"}  body-1-medium ${
      rounded ? rounded : "rounded-lg"
    }`,
    medium: `${customPadding ? customPadding : "px-3 py-1.75"} body-1-medium ${
      rounded ? rounded : "rounded-lg"
    }`,
    small: `${customPadding ? customPadding : "px-2 py-0.75"} body-2-medium ${
      rounded ? rounded : "rounded-lg"
    }`,
    xsmall: `${customPadding ? customPadding : "px-2 py-0.75"} body-3-medium ${
      rounded ? rounded : "rounded-md"
    }`,
    xssmall: `${
      customPadding ? customPadding : "px-1.5 py-0.75"
    } body-4-bold ${rounded ? rounded : "rounded-md"}`,
    icon: `${customPadding ? customPadding : "p-1.5"} ${
      rounded ? rounded : "rounded-lg"
    }`,
  };

  const buttonStyles = `
      ${sizeStyles[size]}
      ${color}
      ${textColor}
      ${borderColor}
    `;

  const iconMarginClass = iconPosition === "left" ? iconMargin : "ml-1";

  // Live Tracking Effect (Scroll & Resize)
  useEffect(() => {
    if (showTooltip && buttonRef.current) {
      const updatePosition = () => {
        if (buttonRef.current) {
          setDropdownPosition(buttonRef);
        }
      };

      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [showTooltip, setDropdownPosition]);

  // Immediate Failsafe Effect
  useEffect(() => {
    if (showTooltip && buttonRef.current) {
      setDropdownPosition(buttonRef);
    }
  }, [showTooltip, setDropdownPosition]);

  // Centralized "Open" Logic
  const handleMouseEnterInternal = (e: React.MouseEvent) => {
    if (disabled && disabledMessage) {
      if (buttonRef.current) {
        setDropdownPosition(buttonRef);
      }
      setShowTooltip(true);
    }
    onMouseEnter?.(e);
  };

  const handleMouseLeaveInternal = (e: React.MouseEvent) => {
    if (disabled && disabledMessage) {
      setShowTooltip(false);
    }
    onMouseLeave?.(e);
  };

  // The reason must be reachable without a mouse. A keyboard or screen-reader
  // user lands on a greyed-out control and, until this, had no way to learn why
  // it is refused — the explanation existed only on `mouseenter`, which also
  // means touch never saw it either.
  const handleFocusInternal = () => {
    if (disabled && disabledMessage) {
      if (buttonRef.current) setDropdownPosition(buttonRef);
      setShowTooltip(true);
    }
  };
  const handleBlurInternal = () => {
    if (disabled && disabledMessage) setShowTooltip(false);
  };

  return (
    <>
      <button
        ref={buttonRef}
        onMouseEnter={handleMouseEnterInternal}
        onMouseLeave={handleMouseLeaveInternal}
        onFocus={handleFocusInternal}
        onBlur={handleBlurInternal}
        // Announced as disabled, not merely painted that way. The element kept
        // its enabled semantics and only dropped `onClick`, so assistive tech
        // presented an ordinary button that silently did nothing when pressed.
        // `aria-disabled` rather than `disabled`: a disabled element is not
        // focusable, and then the reason above becomes unreachable again.
        aria-disabled={disabled || undefined}
        style={{ fontFamily: fontType }}
        type="button"
        data-testid={testId}
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        aria-busy={ariaBusy || undefined}
        title={ariaLabel}
        className={`relative flex flex-row items-center justify-center border transition-all duration-200 ${padding} ${height} ${className} ${margin} ${buttonStyles} ${width} ${
          disabled ? "opacity-50 cursor-not-allowed" : "hover:opacity-80"
        } ${disabled && disabledMessage ? "cursor-help" : ""}`}
        onClick={disabled ? undefined : onClick}
      >
        {loading && (
          <span className="absolute inset-0 flex items-center justify-center">
            <IconLoader2 className="animate-spin" size={20} />
          </span>
        )}
        <span
          className={`flex flex-row items-center justify-center ${loading ? "invisible" : ""}`}
        >
          {Icon && iconPosition === "left" && (
            <Icon size={iconSize} className={`${color} ${iconMarginClass}`} />
          )}
          {text}
          {Icon && iconPosition === "right" && (
            <Icon size={iconSize} className={`${color} ${iconMarginClass}`} />
          )}
        </span>
      </button>
      {announcesReason && !showTooltip && (
        // `hidden`: a description SOURCE only — aria-describedby still reads
        // it and browse mode does not read it a second time (re-audit r4).
        // It is still in the DOM: text queries (RTL getByText, Playwright
        // getByText) DO match it — scope them past `[hidden]` (r5).
        <span id={reasonId} hidden>
          {disabledMessage}
        </span>
      )}
      {showTooltip && disabledMessage && disabled && (
        <PInfo id={reasonId} message={disabledMessage} position={dropdownPosition} />
      )}
    </>
  );
};
